import { create } from "zustand";
import {
  fetchBrands,
  fetchBrandById,
  createBrand,
  updateBrand,
  detectBrandDetails as detectBrandDetailsApi,
  deleteBrand as deleteBrandApi,
  fetchBrandAnalyticsTimeline,
} from "./brandsApi";
import { notifications } from "@mantine/notifications";
import { produce } from "immer";
import i18n from "../i18n";

export const useBrandsStore = create((set, get) => ({
  brands: [],
  isLoading: true,
  analyticsTimeline: {}, // { `${brandId}_${filtersHash}`: timelineData }
  isLoadingAnalytics: false,

  createBrand: async (brandData) => {
    set({ isLoading: true });
    var brandServerData = await createBrand(brandData);
    set({ isLoading: false, brands: [...get().brands, brandServerData] });
    return brandServerData;
  },

  updateBrand: async (brandId, brandData) => {
    set({ isLoading: true });
    try {
      const updatedBrand = await updateBrand(brandId, brandData);
      const brands = get().brands;
      const updatedBrands = brands.map((brand) =>
        brand.id === brandId ? updatedBrand : brand
      );
      set({ brands: updatedBrands, isLoading: false });
      return updatedBrand;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "brands.notifications.update_failed",
          "Failed to update brand"
        ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  detectBrandDetails: async (brandId) => {
    set({ isLoading: true });
    try {
      const updatedBrand = await detectBrandDetailsApi(brandId);
      const brands = get().brands;
      const updatedBrands = brands.map((brand) =>
        brand.id === brandId ? updatedBrand : brand
      );
      set({ brands: updatedBrands, isLoading: false });
      notifications.show({
        title: i18n.t(
          "brands.notifications.detection_started",
          "Discovery started"
        ),
        message: i18n.t(
          "brands.notifications.detection_started_message",
          "We're analyzing your brand in the background — this usually takes 10–30 seconds."
        ),
        color: "blue",
      });
      return updatedBrand;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "brands.notifications.detection_failed",
          "Detection Failed"
        ),
        message:
          err.message ||
          i18n.t(
            "brands.notifications.detection_failed_message",
            "Failed to start brand detection. Make sure you have added a website URL and industry."
          ),
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  /** Force-refresh a single brand from the server. Used when a background job mutates the brand record. */
  refreshBrandById: async (brandId) => {
    try {
      const refreshed = await fetchBrandById(brandId);
      const brands = get().brands ?? [];
      const exists = brands.some((b) => b.id === brandId);
      const next = exists
        ? brands.map((b) => (b.id === brandId ? refreshed : b))
        : [...brands, refreshed];
      set({ brands: next });
      return refreshed;
    } catch {
      // Caller is the polling loop — silent failure is fine; we'll retry on the next tick.
      return null;
    }
  },

  deleteBrand: async (brandId) => {
    set({ isLoading: true });
    try {
      await deleteBrandApi(brandId);
      const brands = get().brands;
      const updatedBrands = brands.filter((brand) => brand.id !== brandId);
      set({ brands: updatedBrands, isLoading: false });
      notifications.show({
        title: i18n.t("brands.notifications.delete_success", "Brand Deleted"),
        message: i18n.t(
          "brands.notifications.delete_success_message",
          "Brand has been successfully deleted."
        ),
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "brands.notifications.delete_failed",
          "Failed to delete brand"
        ),
        message: err.message,
        color: "red",
      });
      set({ isLoading: false });
      throw err;
    }
  },

  fetchBrands: async () => {
    set({ isLoading: true });
    try {
      const brands = await fetchBrands();
      set({
        brands,
        isLoading: false,
      });
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "brands.notifications.fetch_failed",
          "Failed to fetch brands"
        ),
        color: "red",
      });
      set({ isLoading: false });
    }
  },

  getBrandById: async (brandId) => {
    if (!brandId || brandId === "undefined") return null;
    const existingBrand = get().brands?.find((b) => b.id === brandId);
    if (existingBrand) {
      console.log("Brand found in store:", existingBrand);
      return existingBrand;
    }
    console.log("Brand not found in store, fetching:", brandId);
    // If brand is not in the store, fetch it:
    set({ isLoading: true });
    try {
      const brand = await fetchBrandById(brandId);
      set({
        brands: [...(get().brands ?? []), brand],
        isLoading: false,
      });
      return brand;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "brands.notifications.fetch_failed",
          "Failed to fetch brand"
        ),
        color: "red",
      });
    }
  },

  // Analytics timeline functionality
  fetchAnalyticsTimeline: async (brandId, filters = {}) => {
    set({ isLoadingAnalytics: true });
    try {
      const timeline = await fetchBrandAnalyticsTimeline(brandId, filters);
      const filtersHash = btoa(JSON.stringify(filters));
      const cacheKey = `${brandId}_${filtersHash}`;

      set(
        produce((state) => {
          state.analyticsTimeline[cacheKey] = timeline;
          state.isLoadingAnalytics = false;
        })
      );

      return timeline;
    } catch (err) {
      notifications.show({
        title: i18n.t(
          "brands.notifications.analytics_failed",
          "Failed to fetch analytics timeline"
        ),
        message: err.message,
        color: "red",
      });
      set({ isLoadingAnalytics: false });
      throw err;
    }
  },

  getCachedAnalyticsTimeline: (brandId, filters = {}) => {
    const filtersHash = btoa(JSON.stringify(filters));
    const cacheKey = `${brandId}_${filtersHash}`;
    return get().analyticsTimeline[cacheKey];
  },

  clearAnalyticsCache: () => {
    set({ analyticsTimeline: {} });
  },

  // Permission checking methods
  canEditBrand: (brandId) => {
    const brand = get().brands?.find((b) => b.id === brandId);
    return brand?.canEdit ?? false;
  },

  canAnalyzeBrand: (brandId) => {
    const brand = get().brands?.find((b) => b.id === brandId);
    return brand?.canAnalyze ?? false;
  },

  canViewAnalytics: (brandId) => {
    const brand = get().brands?.find((b) => b.id === brandId);
    return brand?.canViewAnalytics ?? false;
  },

  getBrandRelationshipType: (brandId) => {
    const brand = get().brands?.find((b) => b.id === brandId);
    return brand?.relationshipType;
  },

  getBrandRelationship: (brandId) => {
    const brand = get().brands?.find((b) => b.id === brandId);
    if (!brand) return null;

    return {
      relationshipType: brand.relationshipType,
      canEdit: brand.canEdit,
      canAnalyze: brand.canAnalyze,
      canViewAnalytics: brand.canViewAnalytics,
      relationshipCreatedAt: brand.relationshipCreatedAt,
      lastAccessedAt: brand.lastAccessedAt,
      tenantSpecificDescription: brand.tenantSpecificDescription,
      tenantSpecificKeywords: brand.tenantSpecificKeywords,
    };
  },
}));
